import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { TextLink } from "../components/Bits";
import InterestForm from "../components/InterestForm";
import TiltCard from "../components/TiltCard";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { EDITIONS, SHOWS_SO_FAR } from "../lib/editions";
import { UpcomingShows } from "../components/Shows";
import { useUpcomingShows } from "../lib/shows";

export default function Editions() {
  const shows = useUpcomingShows();
  return (
    <>
      <Seo route={ROUTES.editions} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.editions.crumb, path: ROUTES.editions.path }])} />

      <PageHero
        eyebrow="The archive"
        title={<>Every <span className="gold-grad italic">Antara</span> so far</>}
        intro="A running record of the bhajan jamming evenings — where they happened, and what each night was like. New to it all? Start with the "
      >
        <p className="mt-1 font-body text-base md:text-lg" style={{ color: "#E8D9C5" }}>
          <TextLink to={ROUTES.pillar.path}><span style={{ color: "#E5C880" }}>bhajan jamming explainer</span></TextLink>.
        </p>
      </PageHero>

      {/* What is on sale comes first; with nothing ahead, the interest list. */}
      {shows.length ? (
        <UpcomingShows shows={shows} />
      ) : (
        <section className="bg-cream py-20 md:py-28 px-6">
          <InterestForm
            heading={<>Where should Antara go <span className="gold-grad italic">next?</span></>}
            blurb="Tell us which city you're in and the interest list hears first when the next date is announced."
          />
        </section>
      )}

      {/* Past — dark */}
      <section className="bg-[#0F0A07] py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <Eyebrow>Past editions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl md:text-4xl" style={{ color: "#FBF6EE" }}>Where we've sung</h2>
            <p className="mt-3 font-body text-base" style={{ color: "#E8D9C5" }}>{SHOWS_SO_FAR} shows so far, and one format that keeps outgrowing the room.</p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {EDITIONS.map((e, i) => (
              <Reveal key={e.slug} delay={i * 0.1}>
                <TiltCard>
                <Link
                  to={`${ROUTES.editions.path}${e.slug}/`}
                  className="group block h-full rounded-2xl overflow-hidden border transition"
                  style={{ background: "#1C1A17", borderColor: "rgba(201,169,97,0.22)" }}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={e.photos[0].src} alt={e.photos[0].alt} width={e.photos[0].width} height={e.photos[0].height} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="eyebrow" style={{ color: "#C9A961" }}>{e.title ? `${e.title} · ` : ""}{e.dateLabel} · {e.city}{e.shows > 1 ? ` · ${e.shows} shows` : ""}</p>
                    {e.soldOut && (
                      <span className="mt-2 inline-flex rounded-full px-3 py-0.5 font-body text-[11px] font-semibold tracking-[0.14em] uppercase sold-out-badge">Sold out</span>
                    )}
                    <h3 className="mt-2 font-display text-2xl" style={{ color: "#FBF6EE" }}>{e.venue}</h3>
                    <p className="mt-2 font-body text-sm" style={{ color: "#E8D9C5" }}>{e.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-2 font-body text-sm" style={{ color: "#E5C880" }}>
                      Read the recap <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
